import React, { useState } from 'react'
import ItemRow from './item-row'
import ItemSearch from './item-search'

const parse = (value, fallback) => (typeof value === 'string' ? JSON.parse(value) : (value || fallback))

const itemRef = (item) => `${item.type}:${item.id}`

const moveButtonClass = 'p-1 rounded text-gray-600 hover:text-gray-900 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-700 dark:disabled:hover:bg-transparent'

// The slideshow form's items panel: an ordered list of custom slides and
// news articles, with searches to add more. Writes one "Type:id" ref per
// item to `inputName` for the form to save.
const SlideshowItems = ({ initialItems, articlesUrl, slidesUrl, newSlideUrl, inputName }) => {
  const [items, setItems] = useState(() => parse(initialItems, []))

  const refs = new Set(items.map(itemRef))
  const isSelected = (item) => refs.has(itemRef(item))

  const add = (item) => setItems(current => (
    current.some(i => itemRef(i) === itemRef(item)) ? current : [...current, item]
  ))

  const remove = (item) => setItems(current => current.filter(i => itemRef(i) !== itemRef(item)))

  const move = (index, offset) => setItems(current => {
    const target = index + offset
    if (target < 0 || target >= current.length) return current
    const next = [...current]
    ;[next[index], next[target]] = [next[target], next[index]]
    return next
  })

  return (
    <div className='space-y-6'>
      {/* An empty value lets removing every item clear the slideshow */}
      <input type='hidden' name={inputName} value='' />
      {items.map(item => (
        <input key={itemRef(item)} type='hidden' name={inputName} value={itemRef(item)} />
      ))}

      <div>
        <h3 className='text-sm font-medium text-gray-700 dark:text-gray-300'>Slides ({items.length})</h3>
        {items.length === 0 ? (
          <p className='mt-2 text-sm text-gray-500 dark:text-gray-400'>No slides yet. Add a news article or slide below.</p>
        ) : (
          <ul className='divide-y divide-gray-200 dark:divide-gray-700'>
            {items.map((item, index) => (
              <ItemRow key={itemRef(item)} item={item}>
                <button type='button' onClick={() => move(index, -1)} disabled={index === 0} aria-label={`Move ${item.title} up`} className={moveButtonClass}>
                  <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24' aria-hidden='true'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 15l7-7 7 7' />
                  </svg>
                </button>
                <button type='button' onClick={() => move(index, 1)} disabled={index === items.length - 1} aria-label={`Move ${item.title} down`} className={moveButtonClass}>
                  <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24' aria-hidden='true'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                  </svg>
                </button>
                {item.edit_url && (
                  <a href={item.edit_url} target='_blank' rel='noopener noreferrer' className='text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300'>
                    Edit
                  </a>
                )}
                <button type='button' onClick={() => remove(item)} className='text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300'>
                  Remove
                </button>
              </ItemRow>
            ))}
          </ul>
        )}
      </div>

      <ItemSearch
        id='slideshow-article-search'
        label='Add news article'
        placeholder='Search news articles…'
        url={articlesUrl}
        emptyText='No matching articles.'
        isSelected={isSelected}
        onAdd={add}
      />

      <ItemSearch
        id='slideshow-slide-search'
        label='Add existing slide'
        placeholder='Search slides…'
        url={slidesUrl}
        emptyText='No matching slides.'
        isSelected={isSelected}
        onAdd={add}
      />

      {newSlideUrl && (
        <div>
          <a href={newSlideUrl} className='inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-100'>
            New slide
          </a>
        </div>
      )}
    </div>
  )
}

export default SlideshowItems
